/**
 * penalesScene.js v5
 *
 * Perspectiva replicada exactamente de la imagen de referencia:
 *  - Campo de césped plano que ocupa todo el fondo inferior
 *  - Arco centrado, más arriba
 *  - Pelota grande en el punto penal en primer plano
 *  - Área marcada con líneas simples en perspectiva suave
 *  - Arquero parado sobre la línea de gol (pies en la base del arco)
 *  - Zona roja = atajado, verde = gol
 */
import Phaser from 'phaser';

import keeperIdleImg  from './assets/arquero_ataja_abajo_med.png';
import keeperRightImg from './assets/arquero_ataja_abajo_der.png';
import keeperLeftImg  from './assets/arquero_ataja_abajo_izq.png';
import keeperTopImg   from './assets/arquero_ataja_arriba_med.png';

// Nuevas imágenes: ataja/falla × dirección × altura
import keeperAtajaAbajoIzqImg  from './assets/arquero_ataja_abajo_izq.png';
import keeperFallaAbajoIzqImg  from './assets/arquero_falla_abajo_izq.png';
import keeperAtajaArribaIzqImg from './assets/arquero_ataja_arriba_izq.png';
import keeperFallaArribaIzq1Img from './assets/arquero_falla_arriba_izq1.png';
import keeperFallaArribaIzq2Img from './assets/arquero_falla_arriba_izq2.png';
import keeperAtajaAbajoMedImg  from './assets/arquero_ataja_abajo_med.png';
import keeperFallaAbajoMedImg  from './assets/arquero_falla_abajo_med.png';
import keeperAtajaArribaMedImg from './assets/arquero_ataja_arriba_med.png';
import keeperFallaArribaMedImg from './assets/arquero_falla_arriba_med.png';
import keeperAtajaAbajoDerImg  from './assets/arquero_ataja_abajo_der.png';
import keeperFallaAbajoDerImg  from './assets/arquero_falla_abajo_der.png';
import keeperAtajaArribaDerImg from './assets/arquero_ataja_arriba_der.png';
import keeperFallaArribaDer1Img from './assets/arquero_falla_arriba_der1.png';
import keeperFallaArribaDer2Img from './assets/arquero_falla_arriba_der2.png';
import keeperInicialImg from './assets/arquero_inicial.png';
import ballImg  from './assets/pelota1.png';

const ZONA_POS = [
  { row: 0, col: 0 }, { row: 0, col: 1 }, { row: 0, col: 2 },
  { row: 1, col: 0 }, { row: 1, col: 1 }, { row: 1, col: 2 },
];

const ZONA_SPRITE = {
  0: 'k_ataja_arriba_izq',
  1: 'k_ataja_arriba_med',
  2: 'k_ataja_arriba_der',
  3: 'k_ataja_abajo_izq',
  4: 'k_ataja_abajo_med',
  5: 'k_ataja_abajo_der',
};

export default class PenalesScene extends Phaser.Scene {
  constructor() {
    super({ key: 'PenalesScene' });
    this.zonasActivas = [0, 1, 2, 3, 4, 5];
    this.shooting = false;
    this.gameOver = false;
    this.onZonaClick = null;
    this._trail = [];
    this._keeperSprite = null;
    this._pelotaX = 0;
    this._pelotaY = 0;
  }

  preload() {
    this.load.image('ball',ballImg);
    // Nuevas imágenes resultado
    this.load.image('keeper_inicial', keeperInicialImg);
    this.load.image('k_ataja_abajo_izq',  keeperAtajaAbajoIzqImg);
    this.load.image('k_falla_abajo_izq',  keeperFallaAbajoIzqImg);
    this.load.image('k_ataja_arriba_izq', keeperAtajaArribaIzqImg);
    this.load.image('k_falla_arriba_izq1', keeperFallaArribaIzq1Img);
    this.load.image('k_falla_arriba_izq2', keeperFallaArribaIzq2Img);
    this.load.image('k_ataja_abajo_med',  keeperAtajaAbajoMedImg);
    this.load.image('k_falla_abajo_med',  keeperFallaAbajoMedImg);
    this.load.image('k_ataja_arriba_med', keeperAtajaArribaMedImg);
    this.load.image('k_falla_arriba_med', keeperFallaArribaMedImg);
    this.load.image('k_ataja_abajo_der',  keeperAtajaAbajoDerImg);
    this.load.image('k_falla_abajo_der',  keeperFallaAbajoDerImg);
    this.load.image('k_ataja_arriba_der', keeperAtajaArribaDerImg);
    this.load.image('k_falla_arriba_der1', keeperFallaArribaDer1Img);
    this.load.image('k_falla_arriba_der2', keeperFallaArribaDer2Img);
  }

  create() {
    const W = this.scale.width;
    const H = this.scale.height;
    this._build(W, H);
  }

  _build(W, H) {
    this.children.list.slice().forEach(c => c.destroy());
    this._trail = [];
    this._keeperSprite = null;

    this._horizY   = H * 0.52;
    this._goalW    = W * 0.54;
    this._goalH    = H * 0.34;
    this._goalX    = (W - this._goalW) / 2;
    this._goalY    = this._horizY - this._goalH;
    this._spotX    = W / 2;
    this._spotY    = H * 0.80;

    this.arcoRect = {
      x: this._goalX,
      y: this._goalY,
      w: this._goalW,
      h: this._goalH,
    };

    this._drawSky(W, H);
    this._drawCrowd(W, H);
    this._drawField(W, H);
    this._drawGoal(W, H);
    this._createKeeperSprite(W, H);
    this._createBall(W, H);
    this._createZones(W, H);
  }

  _drawSky(W, H) {
    const g = this.add.graphics();
    const horizY = this._horizY;

    for (let i = 0; i < 10; i++) {
      const t = i / 10;
      const r  = Math.round(1  + 4  * t);
      const gv = Math.round(5  + 14 * t);
      const b  = Math.round(14 + 28 * t);
      g.fillStyle((r << 16) | (gv << 8) | b, 1);
      g.fillRect(0, (horizY / 10) * i, W, horizY / 10 + 1);
    }

    const rng = new Phaser.Math.RandomDataGenerator(['sky5']);
    for (let i = 0; i < 50; i++) {
      g.fillStyle(0xffffff, rng.realInRange(0.1, 0.6));
      g.fillCircle(rng.between(0, W), rng.between(0, horizY * 0.8), rng.realInRange(0.4, 1.5));
    }
  }

  _drawCrowd(W, H) {
    const g = this.add.graphics();
    const tTop = 0, tBot = this._horizY;
    const tH = tBot - tTop;

    for (let i = 0; i < 6; i++) {
      const c = Phaser.Display.Color.Interpolate.RGBWithRGB(3, 8, 20, 7, 20, 45, 6, i);
      g.fillStyle(Phaser.Display.Color.GetColor(c.r, c.g, c.b), 0.92);
      g.fillRect(0, tTop + (tH / 6) * i, W, tH / 6 + 1);
    }

    const rng = new Phaser.Math.RandomDataGenerator(['cr5']);
    for (let row = 0; row < 3; row++) {
      const rowY = tTop + (tH / 3) * row + (tH / 3) * 0.82;
      const count = Math.floor(W / 11) + 2;
      for (let p = 0; p < count; p++) {
        const px = (W / count) * p + rng.between(-3, 3);
        const ph = rng.between(5, 12), pw = rng.between(4, 7);
        const col = rng.pick([0x0b1f38, 0x153650, 0x4a88b8, 0x80aed0, 0xffffff]);
        g.fillStyle(col, rng.realInRange(0.18, 0.52));
        g.fillRoundedRect(px - pw / 2, rowY - ph, pw, ph, 1.5);
        g.fillCircle(px, rowY - ph - 3, pw * 0.38);
      }
    }

    [{ x: W * 0.05, y: H * 0.04 }, { x: W * 0.95, y: H * 0.04 }].forEach(pos => {
      g.fillStyle(0xfff8cc, 0.022);
      g.fillTriangle(pos.x, pos.y, pos.x - W * 0.2, tBot, pos.x + W * 0.2, tBot);
      g.fillStyle(0x1a2a3a, 1); g.fillRect(pos.x - 3, pos.y, 6, H * 0.26);
      g.fillStyle(0x2a3a4a, 1); g.fillRect(pos.x - 14, pos.y - 3, 28, 9);
      for (let i = 0; i < 4; i++) {
        g.fillStyle(0xffffcc, 0.9); g.fillCircle(pos.x - 8 + i * 5.5, pos.y + 2, 2.8);
        g.fillStyle(0xffffff, 0.07); g.fillCircle(pos.x - 8 + i * 5.5, pos.y + 2, 5.5);
      }
    });
  }

  _drawField(W, H) {
    const g = this.add.graphics();
    const fieldTop = this._horizY;
    const fieldH   = H - fieldTop;

    const STRIPES = 8;
    for (let s = 0; s < STRIPES; s++) {
      const y0 = fieldTop + fieldH * (s / STRIPES);
      const y1 = fieldTop + fieldH * ((s + 1) / STRIPES);
      const col = s % 2 === 0 ? 0x1d8038 : 0x176b2e;
      g.fillStyle(col, 1);
      g.fillRect(0, y0, W, y1 - y0);
    }

    const cx = W / 2;
    const fieldBot = H;

    const areaW    = W * 0.67;
    const areaTopY = fieldTop + fieldH * 0.02;
    const areaBotY = fieldTop + fieldH * 0.38;

    g.lineStyle(2, 0xffffff, 0.72);

    g.lineBetween(0, areaTopY, W, areaTopY);
    g.lineBetween(cx - areaW / 2, areaBotY, cx + areaW / 2, areaBotY);
    g.lineBetween(cx - areaW / 2, areaTopY, cx - areaW / 2, areaBotY);
    g.lineBetween(cx + areaW / 2, areaTopY, cx + areaW / 2, areaBotY);

    g.fillStyle(0xffffff, 0.90);
    g.fillCircle(this._spotX, this._spotY, 7);

    g.fillStyle(0x000000, 0.06);
    g.fillRect(0, H * 0.88, W, H * 0.12);
  }

  _drawGoal(W, H) {
    const { x: gX, y: gY, w: gW, h: gH } = this.arcoRect;
    const g = this.add.graphics();

    g.fillStyle(0x000a18, 0.92);
    g.fillRect(gX + 6, gY + 2, gW - 12, gH - 2);

    const nC = 20, nR = 12;
    g.lineStyle(0.7, 0xddeeff, 0.20);
    for (let i = 0; i <= nC; i++) {
      const nx = gX + (gW / nC) * i;
      g.lineBetween(nx, gY, nx, gY + gH);
    }
    for (let i = 0; i <= nR; i++) {
      const ny = gY + (gH / nR) * i;
      const sh = (i / nR) * gW * 0.012;
      g.lineBetween(gX + sh, ny, gX + gW - sh, ny);
    }
    g.lineStyle(0.5, 0xddeeff, 0.09);
    for (let i = 0; i <= 8; i++) {
      const sx = gX + (gW / 8) * i;
      g.lineBetween(sx, gY, W / 2 + (sx - W / 2) * 0.12, gY - 13);
    }

    const POST = 10;
    g.fillStyle(0x000000, 0.22);
    g.fillRect(gX - POST / 2 + 4, gY - POST / 2 - 2, gW + POST, POST + 2);
    g.fillRect(gX - POST / 2 + 4, gY, POST, gH + POST + 2);
    g.fillRect(gX + gW - POST / 2 + 4, gY, POST, gH + POST + 2);

    const drawPost = (px, py, pw, ph) => {
      g.fillStyle(0xf0f0f0, 1); g.fillRect(px, py, pw, ph);
      g.fillStyle(0xffffff, 0.88); g.fillRect(px, py, pw * 0.28, ph);
      g.fillStyle(0xb8b8b8, 0.80); g.fillRect(px + pw * 0.68, py, pw * 0.32, ph);
    };
    drawPost(gX - POST / 2, gY - POST / 2, POST, gH + POST);
    drawPost(gX + gW - POST / 2, gY - POST / 2, POST, gH + POST);
    drawPost(gX - POST / 2, gY - POST / 2, gW + POST, POST);

    g.fillStyle(0x000000, 0.15);
    g.fillEllipse(W / 2, this._horizY + 6, gW * 1.05, 16);

    g.lineStyle(1.5, 0xaaddff, 0.08);
    g.strokeRect(gX - POST / 2 - 1, gY - POST / 2 - 1, gW + POST + 2, gH + POST + 2);
  }

   _KEEPER_CONFIG = {
    // Nuevas — ajustar originX/Y/contentH cuando lleguen las imágenes reales
    keeper_inicial: { originX: 0.500, originY: 0.997, contentH: 304, targetFrac: 0.22 }, // pose inicial
    // ⚙️ AJUSTE TAMAÑO: targetFrac controla altura del arquero relativa al arco
    // abajo izq/der: 0.40 | abajo med (idle): 0.55 | arriba: 0.65
    k_ataja_abajo_izq:  { originX: 0.500, originY: 0.997, contentH: 304, targetFrac: 0.20 },
    k_falla_abajo_izq:  { originX: 0.500, originY: 0.997, contentH: 304, targetFrac: 0.20 },
    k_ataja_arriba_izq: { originX: 0.500, originY: 0.998, contentH: 610, targetFrac: 0.40 },
    k_falla_arriba_izq1: { originX: 0.500, originY: 0.998, contentH: 610, targetFrac: 0.40 },
    k_falla_arriba_izq2: { originX: 0.500, originY: 0.998, contentH: 610, targetFrac: 0.40 },
    k_ataja_abajo_med:  { originX: 0.534, originY: 0.992, contentH: 233, targetFrac: 0.18 }, // ← idle
    k_falla_abajo_med:  { originX: 0.534, originY: 0.992, contentH: 233, targetFrac: 0.16 },
    k_ataja_arriba_med: { originX: 0.505, originY: 0.998, contentH: 610, targetFrac: 0.40 },
    k_falla_arriba_med: { originX: 0.505, originY: 0.998, contentH: 610, targetFrac: 0.40 },
    k_ataja_abajo_der:  { originX: 0.500, originY: 0.990, contentH: 305, targetFrac: 0.20 },
    k_falla_abajo_der:  { originX: 0.500, originY: 0.990, contentH: 305, targetFrac: 0.20 },
    k_ataja_arriba_der: { originX: 0.500, originY: 0.998, contentH: 610, targetFrac: 0.40 },
    k_falla_arriba_der1: { originX: 0.500, originY: 0.998, contentH: 610, targetFrac: 0.40 },
    k_falla_arriba_der2: { originX: 0.500, originY: 0.998, contentH: 610, targetFrac: 0.40 },
  };


  // Mapeo zona + resultado → texture key
  // zonaId = donde pateó el jugador, zonaArqueroId = donde fue el arquero
  _getKeeperResultKey(zonaArqueroId, gol, zonaId) {
    // ZONAS: 0=ArrIzq, 1=ArrCen, 2=ArrDer, 3=AbaIzq, 4=AbaCen, 5=AbaDer
    const resultado = gol ? 'falla' : 'ataja'; // gol = arquero falló, atajado = arquero atajó

    // Variantes para falla arriba: si el jugador pateó a la zona de abajo correspondiente
    // el arquero queda mirando para atrás → variante 2. Sino → variante 1
    if (resultado === 'falla') {
      if (zonaArqueroId === 0) {
        // arquero voló arriba izq
        const variante = zonaId === 3 ? '2' : '1'; // zona 3 = abajo izq → mira para atrás
        return `k_falla_arriba_izq${variante}`;
      }
      if (zonaArqueroId === 2) {
        // arquero voló arriba der
        const variante = zonaId === 5 ? '2' : '1'; // zona 5 = abajo der → mira para atrás
        return `k_falla_arriba_der${variante}`;
      }
    }

    const MAP = {
      0: `k_${resultado}_arriba_izq1`,  // fallback (solo para ataja)
      1: `k_${resultado}_arriba_med`,
      2: `k_${resultado}_arriba_der1`,  // fallback (solo para ataja)
      3: `k_${resultado}_abajo_izq`,
      4: `k_${resultado}_abajo_med`,
      5: `k_${resultado}_abajo_der`,
    };
    return MAP[zonaArqueroId] || 'keeper_idle';
  }

  _createKeeperSprite(W, H) {
    const { y: gY, h: gH } = this.arcoRect;
    const kX = W / 2;
    const kY = gY + gH;

    const cfg = this._KEEPER_CONFIG['keeper_inicial'];
    const scale = (gH * cfg.targetFrac) / cfg.contentH;

    const spr = this.add.image(kX, kY, 'keeper_inicial');
    spr.setOrigin(cfg.originX, cfg.originY);
    spr.setScale(scale);

    this._keeperBase   = { x: kX, y: kY };
    this._keeperSprite = spr;
  }

  _setKeeperTexture(key) {
    if (!this._keeperSprite) return;
    const cfg = this._KEEPER_CONFIG[key];
    if (!cfg) return;
    const gH = this.arcoRect.h;
    const scale = (gH * cfg.targetFrac) / cfg.contentH;
    this._keeperSprite.setTexture(key);
    this._keeperSprite.setOrigin(cfg.originX, cfg.originY);
    this._keeperSprite.setScale(scale);
  }

  _createBall(W, H) {
    const ballDiameter = 44;

    this.ballSprite = this.add.image(this._spotX, this._spotY, 'ball');
    const scale = ballDiameter / this.ballSprite.width;
    this.ballSprite.setScale(scale);
    this.ballSprite.setOrigin(0.4986, 0.4986);
    this._ballBaseScale = scale;

    this.ballGfx = this.add.graphics();
    this._pelotaX = this._spotX;
    this._pelotaY = this._spotY;
    this._drawBallShadow(this._pelotaX, this._pelotaY, ballDiameter / 2);
  }

  _drawBallShadow(x, y, r) {
    const g = this.ballGfx;
    g.clear();
    this._trail.forEach((t, i) => {
      const a  = (i / this._trail.length) * 0.09;
      const tr = r * (i / this._trail.length) * 0.55;
      if (tr > 1.5) { g.fillStyle(0xffffff, a); g.fillCircle(t.x, t.y, tr); }
    });
    g.fillStyle(0x000000, 0.22);
    g.fillEllipse(x + 2, y + r + 3, r * 2.2, r * 0.45);
  }

  _createZones(W, H) {
    if (this._zonaObjs)  this._zonaObjs.forEach(z => z.destroy());
    if (this._zonaGlows) this._zonaGlows.forEach(z => z.destroy());
    this._zonaObjs = []; this._zonaGlows = [];

    const { x: gX, y: gY, w: gW, h: gH } = this.arcoRect;
    const colW = gW / 3, rowH = gH / 2, PAD = 5;

    ZONA_POS.forEach((pos, id) => {
      const zx = gX + pos.col * colW + PAD;
      const zy = gY + pos.row * rowH + PAD;
      const zw = colW - PAD * 2, zh = rowH - PAD * 2;

      const glowG = this.add.graphics(); glowG.setAlpha(0);
      this._zonaGlows.push(glowG);

      const zone = this.add.graphics();
      zone.fillStyle(0xffffff, 0.001); zone.fillRect(zx, zy, zw, zh);
      zone.setInteractive(
        new Phaser.Geom.Rectangle(zx, zy, zw, zh),
        Phaser.Geom.Rectangle.Contains
      );
      zone.setData('id', id);
      zone.setData('zx', zx); zone.setData('zy', zy);
      zone.setData('zw', zw); zone.setData('zh', zh);
      this._zonaObjs.push(zone);

      // Glow idle inicial en todas las zonas activas
      this._restoreZoneGlowIdle(glowG, zx, zy, zw, zh);

      zone.on('pointerover', () => {
        if (this.shooting || !this.zonasActivas.includes(id)) return;
        this._showZoneGlow(glowG, zx, zy, zw, zh, 0x00d4ff, 0.18, false);
      });
      zone.on('pointerout', () => {
        if (this.shooting) return;
        // Restaurar glow idle si la zona sigue activa
        if (this.zonasActivas.includes(id)) {
          this._restoreZoneGlowIdle(glowG, zx, zy, zw, zh);
        } else {
          this.tweens.add({ targets: glowG, alpha: 0, duration: 150 });
        }
      });
      zone.on('pointerdown', () => {
        if (this.shooting || this.gameOver || !this.zonasActivas.includes(id)) return;
        this.shooting = true; // Guard inmediato — bloquea spam antes de cualquier callback
        this._showZoneGlow(glowG, zx, zy, zw, zh, 0x00d4ff, 0.30, false);
        if (this.onZonaClick) this.onZonaClick(id);
      });
    });
  }

  _showZoneGlow(g, x, y, w, h, col, alpha, isResult) {
    g.clear();
    for (let i = 4; i >= 0; i--) {
      const ex = i * 3;
      g.lineStyle(isResult ? 2 : 1.5, col, (1 - i / 4) * alpha * 0.55);
      g.strokeRect(x - ex, y - ex, w + ex * 2, h + ex * 2);
    }
    g.fillStyle(col, alpha * (isResult ? 0.45 : 0.30));
    g.fillRect(x, y, w, h);
    g.lineStyle(isResult ? 3 : 2, col, isResult ? 1.0 : 0.88);
    g.strokeRect(x, y, w, h);
    g.setAlpha(1);
  }

  mostrarResultadoZona(zonaId, gol) {
    const zone = this._zonaObjs[zonaId];
    const glowG = this._zonaGlows[zonaId];
    if (!zone || !glowG) return;
    const zx = zone.getData('zx'), zy = zone.getData('zy');
    const zw = zone.getData('zw'), zh = zone.getData('zh');
    const col = gol ? 0x00ff88 : 0xff2244;
    this._showZoneGlow(glowG, zx, zy, zw, zh, col, 0.55, true);
  }

  // ── ÚNICO CAMBIO: onImpacto se llama cuando la pelota llega al arco ─────────
  animarDisparo(zonaId, zonaArqueroId, gol, onComplete, onImpacto) {
    this.shooting = true;
    const W = this.scale.width, H = this.scale.height;
    const { x: gX, y: gY, w: gW, h: gH } = this.arcoRect;
    const colW = gW / 3, rowH = gH / 2;

    const zP = ZONA_POS[zonaId];
    let tX = gX + zP.col * colW + colW / 2;
    let tY = gY + zP.row * rowH + rowH / 2;
    // Zona 4 (abajo centro) + gol: desplazar pelota arriba-derecha para no pisarse con arquero
    if (zonaId === 4) { tX += colW * 0.25; tY -= rowH * 0.30; }

    const aP = ZONA_POS[zonaArqueroId];
    const jX = gX + aP.col * colW + colW / 2;
    const jY = aP.row === 0
      ? gY + rowH * 0.4
      : gY + gH - rowH * 0.1;

    this._pelotaX = this._spotX; this._pelotaY = this._spotY;
    this._trail = [];
    if (this.ballSprite) {
      this.ballSprite.setPosition(this._spotX, this._spotY);
      this.ballSprite.setScale(this._ballBaseScale);
    }
    this._drawBallShadow(this._pelotaX, this._pelotaY, 20);

    // Usar imagen específica de resultado si existe, sino fallback al sprite genérico
    const resultKey = this._getKeeperResultKey(zonaArqueroId, gol, zonaId);
    const sprKey = (this._KEEPER_CONFIG[resultKey]) ? resultKey : (ZONA_SPRITE[zonaArqueroId] || 'k_ataja_abajo_med');
    this._setKeeperTexture(sprKey);
    // Nota: la exclusión de zona 1 cuando zonaId===4 se hace en penalesLogic.js

    this.tweens.add({
      // ⚙️ VELOCIDAD PELOTA: duration en ms (más alto = más lento)
      targets: this, _pelotaX: tX, _pelotaY: tY,
      duration: 480, ease: 'Cubic.easeIn',
      onUpdate: (tw) => {
        this._trail.push({ x: this._pelotaX, y: this._pelotaY });
        if (this._trail.length > 8) this._trail.shift();
        const progress = tw.progress;
        const r = 20 - progress * 14;
        if (this.ballSprite) {
          this.ballSprite.setPosition(this._pelotaX, this._pelotaY);
          const s = this._ballBaseScale * (1 - progress * 0.60);
          this.ballSprite.setScale(Math.max(s, this._ballBaseScale * 0.25));
          this.ballSprite.angle += 8;
        }
        this._drawBallShadow(this._pelotaX, this._pelotaY, r);
      },
      onComplete: () => {
        this._trail = [];
        if (this.ballSprite) {
          if (gol) {
            // Gol: pelota queda visible en el arco (pequeña)
            this.ballSprite.setPosition(tX, tY);
            // ⚙️ TAMAÑO PELOTA GOL: escala final al llegar al arco
            this.ballSprite.setScale(this._ballBaseScale * 0.55);
          } else {
            // Atajada: la imagen del arquero ya tiene la pelota, ocultamos la pelota sprite
            this.ballSprite.setVisible(false);
          }
        }
        this.ballGfx.clear();

        // ── Sonido: pelota llega al arco ──
        if (onImpacto) onImpacto();

        this.cameras.main.shake(85, 0.005);
        this.mostrarResultadoZona(zonaId, gol);

        const fl = this.add.graphics();
        const flashCol = gol ? 0x00ff88 : 0xff2244;
        fl.fillStyle(flashCol, 0.32); fl.fillCircle(tX, tY, 22);
        fl.lineStyle(2.5, flashCol, 0.85); fl.strokeCircle(tX, tY, 22);
        this.tweens.add({
          targets: fl, alpha: 0, scaleX: 1.8, scaleY: 1.8, duration: 380,
          onComplete: () => fl.destroy(),
        });

        if (gol) this._spawnParticles(tX, tY);

        setTimeout(() => {
          this._resetBall();
          this._resetKeeperGlows();
          this._resetKeeper(W, H);
          if (!this.gameOver) this.shooting = false;
          if (onComplete) onComplete();
        }, 900);
      }
    });

    const spr = this._keeperSprite;
    // Sin movimiento para: abajo centro (4) y arriba centro (1)
    const sinMovimiento = zonaArqueroId === 4 || zonaArqueroId === 1;
    if (spr && !sinMovimiento) {
      let targetY;
      if (aP.row === 0) {
        // ⚙️ ALTURA SALTO ARRIBA: fracción del alto del arco (más alto = sube más)
        targetY = gY + gH * 0.72;  // era 0.60 — menos altura
      } else {
        // ⚙️ ALTURA SALTO ABAJO: fracción del alto del arco (más alto = baja menos)
        targetY = gY + gH * 0.92;  // era 0.88 — más pegado al piso
      }

      // ⚙️ VELOCIDAD ARQUERO: duration en ms (más alto = más lento)
      this.tweens.add({
        targets: spr,
        x: jX, y: targetY,
        duration: 380,
        ease: 'Quad.easeOut',
        delay: 55,
      });
    }
  }

  // Glow idle siempre visible en zonas activas
  _restoreZoneGlowIdle(g, x, y, w, h) {
    g.clear();
    g.fillStyle(0x00d4ff, 0.04);
    g.fillRect(x, y, w, h);
    g.lineStyle(1.5, 0x00d4ff, 0.28);
    g.strokeRect(x, y, w, h);
    g.setAlpha(1);
  }

  _resetBall() {
    this._pelotaX = this._spotX; this._pelotaY = this._spotY;
    this._trail = [];
    if (this.ballSprite) {
      this.ballSprite.setVisible(true); // restaurar si estaba oculta (atajada)
      this.ballSprite.setPosition(this._spotX, this._spotY);
      this.ballSprite.setScale(this._ballBaseScale);
      this.ballSprite.angle = 0;
    }
    this._drawBallShadow(this._pelotaX, this._pelotaY, 20);
  }

  _resetKeeperGlows() {
    this._zonaGlows?.forEach((g, id) => {
      g.clear();
      if (this.zonasActivas.includes(id) && this._zonaObjs?.[id]) {
        const zone = this._zonaObjs[id];
        const zx = zone.getData('zx'), zy = zone.getData('zy');
        const zw = zone.getData('zw'), zh = zone.getData('zh');
        this._restoreZoneGlowIdle(g, zx, zy, zw, zh);
      } else {
        g.setAlpha(0);
      }
    });
  }

  _resetKeeper(W, H) {
    const spr  = this._keeperSprite;
    const base = this._keeperBase;
    if (!spr || !base) return;
    // Cancelar cualquier tween previo del sprite para evitar conflictos por spam
    this.tweens.killTweensOf(spr);
    this.tweens.add({
      targets: spr, x: base.x, y: base.y,
      duration: 400, ease: 'Back.easeOut',
      onComplete: () => {
        // Solo cambiar textura si no estamos en medio de otro disparo
        if (!this.shooting) this._setKeeperTexture('keeper_inicial');
      },
    });
  }

  _spawnParticles(ox, oy) {
    const COLS = [0x00ff88, 0x00f2ff, 0xffd700, 0xff4488, 0xffffff];
    for (let i = 0; i < 18; i++) {
      let vx = Phaser.Math.FloatBetween(-3, 3);
      let vy = Phaser.Math.FloatBetween(-4, -1.2);
      let px = ox + Phaser.Math.Between(-18, 18);
      let py = oy + Phaser.Math.Between(-6, 6);
      let life = Phaser.Math.Between(20, 48);
      const col = COLS[i % COLS.length];
      const pw = Phaser.Math.Between(4, 9), ph = Phaser.Math.Between(3, 5);
      const pg = this.add.graphics();
      this.time.addEvent({
        delay: 16, repeat: life,
        callback: () => {
          if (!pg.scene) return;
          px += vx; py += vy; vy += 0.13; life -= 1;
          pg.clear();
          if (life > 0) { pg.fillStyle(col, Math.min(1, life / 16)); pg.fillRect(px, py, pw, ph); }
          else pg.destroy();
        }
      });
    }
  }

  setZonasActivas(ids) {
    this.zonasActivas = ids;
    this._zonaObjs?.forEach((zone, id) => {
      const activa = ids.includes(id);
      zone.setAlpha(activa ? 1 : 0.22);
      const gfx = this._zonaGlows[id];
      if (!gfx) return;
      const zx = zone.getData('zx'), zy = zone.getData('zy');
      const zw = zone.getData('zw'), zh = zone.getData('zh');
      gfx.clear();
      if (activa) {
        this._restoreZoneGlowIdle(gfx, zx, zy, zw, zh);
      } else {
        gfx.fillStyle(0x000000, 0.38); gfx.fillRect(zx, zy, zw, zh);
        gfx.lineStyle(1, 0x222233, 0.5); gfx.strokeRect(zx, zy, zw, zh);
        gfx.setAlpha(1);
      }
    });
  }

  redraw(W, H) { this._build(W, H); }
}